import { useEffect, useState } from 'react'
import { usePage } from '@inertiajs/react'
import type { SharedProps, CelebrateItem } from '@/types'
import { t } from '@/lib/i18n'
import { toast } from '@/components/Toast'
import { buzz, coin, fanfare } from '@/lib/sound'

// earn 的 note 是纯文案（例如「完成「刷牙」」），图标和分值都要前端自己补上。
// streak / early 的 note 在 Points 里已经拼好了图标和分值（例如「🔥 连续打卡 31 天 +20」），
// 原样展示即可——不能再叠加图标或角标，否则就是本文件之前的 bug：ICON[it.t] 对所有
// toast 类型一视同仁地加图标、+{delta} 也一视同仁地加角标，导致两者都被渲染了两遍。
// 这里只登记 earn 一种：Record<'earn', string> 意味着谁想对 streak/early 也套用这张表，
// 类型检查会直接报错，而不是静默地再重复一次。
const ICON: Record<'earn', string> = { earn: '🪙' }

/**
 * 服务端 celebrate toast → 客户端 toast 队列。
 *
 * 自己不再渲染任何 DOM：视觉统一由 <ToastHost> 提供（底部、贴 tab bar 上沿、5 秒、
 * 带排空进度条）。保留这一层的理由是**数据源**不同——连击天数、提前奖励只有
 * 服务端算得出来，客户端没有那个数。
 */
export function ToastStack() {
  const celebrate = usePage<SharedProps>().props.flash?.celebrate

  useEffect(() => {
    const incoming = celebrate?.toasts ?? []
    if (incoming.length === 0) return

    // 丢掉 earn：打卡页与目标详情页现在自己弹一条**带撤销**的客户端 toast，
    // 内容是同一件事（「刷牙 +3 分」），而客户端那条还多一个撤销按钮 ——
    // 两条一起弹只会互相顶掉（后来的顶掉前面的）。
    //
    // streak / early 放行：连击天数与提前奖励只有服务端算得出来，客户端没有那个数。
    //
    // ⚠️ 公开分享页（/u/:uuid）不在 <Layout> 里、根本没有 ToastStack，所以不受影响。
    // 若将来给它加了 toast，它没有客户端包装，要么给它包一层、要么在这里按页面放行。
    incoming.filter((it) => it.t !== 'earn').forEach((it) => toast(toastText(it)))

    // 若这次打卡同时解锁了庆祝卡（目标达成 / 徽章解锁），把打卡音让给它——两段
    // 声音前后脚响会糊成一团，而稀罕的庆祝卡（CelebrationCard 自己会放
    // fanfare + 长震）比每天都有的这条 toast 更值得被听见。
    if (!celebrate?.cards?.length) {
      coin()
      buzz(20)
    }
  }, [celebrate])

  return null
}

function toastText(it: CelebrateItem): string {
  return it.t === 'earn' ? `${ICON.earn} ${it.txt} +${it.delta}` : it.txt
}

// 已经放过号角的那一份庆祝。Inertia 把 props（含 flash）缓存进 history.state，
// 后退键会把同一份 cards 原样复原、让下面的 Effect 再跑一遍——卡片重新出现是合理的
// （那就是被复原的页面状态），号角再响一遍不是。
//
// 记在模块作用域而不是 useRef：本仓库的每个页面各自渲染一次 <Layout>，Inertia 换页
// 时页面组件的类型就变了，整棵树连带 CelebrationCard 一起卸载重挂，ref 会跟着清空——
// 而「按后退键回到刚才那一页」正是要跨过这次重挂才被记住的事。
// 整页刷新时这个变量当然会没，但那时 flash 是一次性的、服务端也不会再下发第二遍。
//
// 只在**有卡**的时候更新它：中间那次没有卡的换页若把它冲成空串，后退回来就又不认识
// 这一份了。代价是「撤销打卡再重新打卡、达成同一个目标」的第二张卡没有号角——
// 卡片照样出现，只是没声；比每次后退都响一遍划算。
let fanfaredCards = ''

/**
 * 居中的庆祝卡：目标达成与成就解锁。需要手动关——稀罕的事才值得打断用户。
 * 一次打卡若同时达成目标又解锁成就，合并为一张卡。
 */
export function CelebrationCard() {
  const celebrate = usePage<SharedProps>().props.flash?.celebrate
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    setDismissed(false)
    if (!celebrate?.cards?.length) return
    const signature = JSON.stringify(celebrate.cards)
    if (signature === fanfaredCards) return
    fanfaredCards = signature
    // 目标达成 / 徽章解锁：比每天都有的打卡 toast 稀罕，这次音效由它来放
    // （ToastStack 那边同一时刻会让掉自己的 coin + 轻震，见那边的注释）。
    fanfare()
    buzz(200)
  }, [celebrate])

  const cards = celebrate?.cards ?? []
  if (dismissed || cards.length === 0) return null

  // done 的 note 自带 🎉（例如「🎉 目标「X」达成」），卡片顶部再摆一个通用 🎉 会跟它重复；
  // 纯 badge 的 note 用的是徽章自己的图标（例如「🌱 解锁成就：初次出发」），不含 🎉，
  // 顶部保留通用 🎉 撑场面就好，不受影响。
  const hasDone = cards.some((c) => c.t === 'done')

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 px-5">
      <div className="bg-card rounded-2xl shadow-xl max-w-[340px] w-full px-6 py-7 text-center">
        {!hasDone && <div className="text-[44px] leading-none mb-3">🎉</div>}
        <div className="flex flex-col gap-2 mb-6">
          {cards.map((c, i) => (
            <div key={`${c.t}-${i}`} className="font-bold text-[15px]">{c.txt}</div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="bg-brand text-white font-semibold px-5 py-2.5 rounded-[10px] w-full"
        >
          {t('celebrate.nice')}
        </button>
      </div>
    </div>
  )
}
